import React from 'react';
import {
  Circle,
  INCH,
  Line,
  Rect,
  SchematicShell,
} from './SchematicPrimitives';
import {
  classifyLaundryArrangement,
  LAUNDRY_SIDE_BY_SIDE,
  LAUNDRY_SINGLE,
} from '../../../utils/laundryArrangement';

/**
 * Washer / dryer — front-load form factor. Schematic is shared
 * between the two types since the front elevation is essentially
 * identical: control strip across the top + large circular door
 * centered in the body. Two separate exported components dispatch
 * via obj.type in the registry so each type has its own entry point.
 *
 * Per the plan note: "usually GLB-backed; only render schematic when
 * modelId is null". The dispatch site in RenderWallElevation calls
 * the schematic only when a GLB lookup fails, so this component does
 * not need to check `obj.modelId` itself.
 *
 * Side views: deep rectangle profile with a small toe-kick at the
 * front bottom, a thin control panel strip at the front top, and the
 * door circle visible in profile as a vertical line near the front
 * face (the door bulges slightly forward). Symmetric left ↔ right.
 */

const CONTROL_STRIP_RATIO = 0.18;
const DOOR_RADIUS_RATIO = 0.42;
const SIDE_TOE_KICK_M = 3 * INCH;

/**
 * Shared front face for washers and dryers. `knobCount` matches the 3D
 * renderer's knob layout: washer = 3 (left + center-left + center-right),
 * dryer = 2 (left + right). The door circle + inner window + LED display
 * stripe complete the front-elevation read.
 */
function WasherDryerFace({
  width,
  height,
  strokeColor,
  strokeWidth,
  knobCount = 2,
  ledWidth = 0.09,
}) {
  const stripH = height * CONTROL_STRIP_RATIO;
  const bodyH = height - stripH;
  const cx = width / 2;
  const cy = stripH + bodyH / 2;
  const r = Math.min(width, bodyH) * DOOR_RADIUS_RATIO;
  const knobR = Math.min(stripH * 0.3, width * 0.04);
  // Mirror laundryRenderers.js knob layout. Washer: x = ±28% spread plus
  // one at -11% (3 knobs clustered to the left of center). Dryer: x = ±30%.
  const knobXs =
    knobCount === 3
      ? [-0.28, -0.11, 0.11].map((p) => width * (0.5 + p))
      : [-0.3, 0.3].map((p) => width * (0.5 + p));
  // LED display block centered on the strip — emissive band on the 3D
  // unit, drawn as a short filled rectangle to read at this size. Dryer's
  // display is slightly wider; pass `ledWidth` from the caller in meters.
  const displayW = Math.min(ledWidth, width * 0.35);
  const displayH = Math.min(stripH * 0.28, displayW * 0.28);
  return (
    <g>
      <Line
        x1={0}
        y1={stripH}
        x2={width}
        y2={stripH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Control knobs */}
      {knobXs.map((kx, i) => (
        <Circle
          key={`knob-${i}`}
          cx={kx}
          cy={stripH / 2}
          r={knobR}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ))}
      {/* LED display — short filled band right of the knob cluster */}
      <rect
        x={width - displayW - Math.min(stripH * 0.4, width * 0.04)}
        y={stripH / 2 - displayH / 2}
        width={displayW}
        height={displayH}
        fill={strokeColor}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      {/* Door — circle inside the body */}
      <Circle
        cx={cx}
        cy={cy}
        r={r}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Inner door window — slightly smaller concentric circle to read
          as a window, not a flat plate. */}
      <Circle
        cx={cx}
        cy={cy}
        r={r * 0.85}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

/**
 * Side profile of a front-load washer/dryer.
 *
 * Canonical "left" orientation: back of the unit at x = 0, front face
 * at x = depth. From this side we see:
 *   - control panel as a thin horizontal band along the top edge,
 *     biased toward the front (since the controls sit at the front-top)
 *   - door bulge: a vertical line a small distance inside the front
 *     face indicates the recessed door pocket / hinge line
 *   - toe-kick: short horizontal line at the bottom-front
 *
 * "right" mirrors via the standard reflection transform.
 */
function WasherDryerSide({ depth, height, view, strokeColor, strokeWidth }) {
  const stripH = Math.min(height * CONTROL_STRIP_RATIO, depth * 0.3);
  const doorLineX = Math.max(depth - depth * 0.18, depth * 0.7);
  const doorY0 = Math.min(stripH + 0.01, height * 0.3);
  const doorY1 = Math.max(height - SIDE_TOE_KICK_M, height * 0.85);
  const toeKickY = Math.max(0, height - SIDE_TOE_KICK_M);
  const toeKickX = Math.min(depth * 0.15, 4 * INCH);
  const content = (
    <g
      transform={
        view === 'right' ? `translate(${depth}, 0) scale(-1, 1)` : undefined
      }
    >
      {/* Control panel band at top — full-width strip, since controls
          run across the unit; the front-bias is conveyed by the
          short vertical tick at the front edge below. */}
      <Line
        x1={0}
        y1={stripH}
        x2={depth}
        y2={stripH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Front edge of control panel: short vertical drop from the
          top corner down to the strip-bottom on the front side. */}
      <Line
        x1={depth * 0.75}
        y1={0}
        x2={depth * 0.75}
        y2={stripH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Door-line just inside the front face. */}
      <Line
        x1={doorLineX}
        y1={doorY0}
        x2={doorLineX}
        y2={doorY1}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Toe-kick recess at front-bottom — short stub from front edge
          inward, plus a riser tick. */}
      {toeKickY < height && (
        <>
          <Line
            x1={depth - toeKickX}
            y1={toeKickY}
            x2={depth}
            y2={toeKickY}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
          <Line
            x1={depth - toeKickX}
            y1={toeKickY}
            x2={depth - toeKickX}
            y2={height}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </>
      )}
    </g>
  );
  return (
    <SchematicShell
      width={depth}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {content}
    </SchematicShell>
  );
}

function LaundryUnitSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
  knobCount,
  ledWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <WasherDryerSide
        depth={depth}
        height={height}
        view={view}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      <WasherDryerFace
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
        knobCount={knobCount}
        ledWidth={ledWidth}
      />
    </SchematicShell>
  );
}

export function WasherSchematic(props) {
  // Washer: 3 knobs + narrower LED display, matching `washerRenderer`.
  return <LaundryUnitSchematic {...props} knobCount={3} ledWidth={0.09} />;
}
WasherSchematic.supportsSideViews = true;

export function DryerSchematic(props) {
  // Dryer: 2 knobs + slightly wider LED display, matching `dryerRenderer`.
  return <LaundryUnitSchematic {...props} knobCount={2} ledWidth={0.11} />;
}
DryerSchematic.supportsSideViews = true;

/**
 * The combined `washerDryer` — RoomPlan captures a washer and dryer as ONE
 * object, so how many machines it holds is inferred from the scanned box's
 * proportions. Both this and `washerDryerRenderer` route through the shared
 * `classifyLaundryArrangement`, so an elevation can never contradict the 3D
 * view of the same object. Washer first in a pair — left when side by side,
 * bottom when stacked.
 *
 * From the side, a stacked pair shows two profiles; a side-by-side pair and a
 * lone machine both show just one, so they reuse the single-unit side.
 */
export function WasherDryerSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const arrangement = classifyLaundryArrangement({ width, height });
  const sideBySide = arrangement === LAUNDRY_SIDE_BY_SIDE;
  const single = arrangement === LAUNDRY_SINGLE;

  const unitW = sideBySide ? width / 2 : width;
  const unitH = sideBySide || single ? height : height / 2;

  // SVG y grows downward, so the stacked dryer (on top in 3D) draws first.
  const units = single
    ? [{ key: 'washer', x: 0, y: 0, knobCount: 3, ledWidth: 0.09 }]
    : sideBySide
      ? [
          { key: 'washer', x: 0, y: 0, knobCount: 3, ledWidth: 0.09 },
          { key: 'dryer', x: unitW, y: 0, knobCount: 2, ledWidth: 0.11 },
        ]
      : [
          { key: 'dryer', x: 0, y: 0, knobCount: 2, ledWidth: 0.11 },
          { key: 'washer', x: 0, y: unitH, knobCount: 3, ledWidth: 0.09 },
        ];

  if (view === 'left' || view === 'right') {
    if (sideBySide || single) {
      return (
        <WasherDryerSide
          depth={depth}
          height={height}
          view={view}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      );
    }
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        {units.map((u) => (
          <g
            key={u.key}
            data-laundry-unit={u.key}
            transform={`translate(0, ${u.y})`}
          >
            <WasherDryerSide
              depth={depth}
              height={unitH}
              view={view}
              strokeColor={strokeColor}
              strokeWidth={strokeWidth}
            />
          </g>
        ))}
      </SchematicShell>
    );
  }

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {units.map((u) => (
        <g
          key={u.key}
          data-laundry-unit={u.key}
          transform={`translate(${u.x}, ${u.y})`}
        >
          {/* Divider between the two machines. */}
          <Rect
            x={0}
            y={0}
            width={unitW}
            height={unitH}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
          <WasherDryerFace
            width={unitW}
            height={unitH}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
            knobCount={u.knobCount}
            ledWidth={u.ledWidth}
          />
        </g>
      ))}
    </SchematicShell>
  );
}
WasherDryerSchematic.supportsSideViews = true;
